import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles9() {
  const { lesson, course, accent } = findLesson("the-beatles", "9");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="Detecting chords" title="Four methods — emotion, function, degree, bass">
        <Prose>
          <p>
            This lesson opens with a framework Jason uses throughout the Beatles course for
            identifying chords by ear. There are four distinct things you can listen for, and
            any one of them can confirm a chord:
          </p>
          <p>
            <strong>Chord emotion</strong> — does it sound happy, sad, or like something you've
            never heard before? Happy usually means major; sad usually means minor. "Whether it
            sounds happy, whether it sounds sad, or whether it sounds like something you may have
            never heard before — you just have to mark it out."
          </p>
          <p>
            <strong>Chord function</strong> — is the chord stable or unstable? Stable chords are
            tonic chords (1 and 6). Unstable chords are dominant (5) or pre-dominant (2 and 4).
            "Chord function will be whether the chord sounds stable or unstable."
          </p>
          <p>
            <strong>Chord degree</strong> — which number in the scale is this chord? Is it the 1,
            the 4, the 5? "Chord degree will tell you if it's the one of the scale, two of the
            scale, three of the scale."
          </p>
          <p>
            <strong>Bass note</strong> — "if the bass is actually playing something, you have to
            figure that out." The bass note names the chord. Whatever the bass plays at beat one
            of a bar is the root.
          </p>
        </Prose>

        <TheoryBox label="Using all four methods together">
          In practice, you use whichever method gives you the clearest answer first, then confirm
          with a second method. Hear a happy chord? It's major — probably C, F, or G in C major.
          Hear unstable motion toward resolution? That's likely a dominant. Hear the bass note?
          That names it. "Trial and error along with going back and forth with the emotions of the
          chords. You hear a chord and immediately you're trying to know that it's a happy chord
          or a sad chord. You can just go back and forth."
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="CGj85pVzRJs"
        title="Let It Be — The Beatles"
        caption="Key: C major. The chords change approximately every two beats. Listen for the bass — it will tell you the chord root at each change. Notice the bass using F♯ as a passing note on its way up to G (Jason's chart: C, C, F♯, G). The Fmaj7 to Fmaj6 happens within one bar of F."
      />

      <Chapter eyebrow="Let It Be" title="C major — Fmaj7 to Fmaj6 in one bar">
        <Prose>
          <p>
            "Let It Be" is in C major. The chord changes happen approximately every two counts —
            faster than you might expect. The basic progression is C → G → A minor → F, but that
            description undersells one of the most interesting details in the song.
          </p>
          <p>
            Within the F bar, the chord doesn't just sit on F major. It moves from <strong>F major 7</strong> to
            <strong> F major 6</strong>, all while the bass stays on F. "What these guys do is
            they play Fmaj7 and Fmaj6 all in one bar." Fmaj7 is F A C E. Fmaj6 is F A C D. The
            bass doesn't move — the upper voices shift by one note. "Even if you say F major
            you'd be fine," Jason says — but listen again: "Don't you think it goes F major 7th and
            then kind of resolves?"
          </p>
          <p>
            One student hears D minor at that moment — and the observation is accurate. Fmaj6
            (F A C D) contains every note of D minor (D F A); with F in the bass, the only extra
            note is C. "You heard a D minor very well,
            but then the bass was still F. So then you have to call it D minor over F. But D minor
            over F we have a name for it — it's F major 6th. What is F major 6th? The answer is
            F A C D." The bass determines the name: with F underneath, the chord is
            named from F, so it's Fmaj6 (D minor 7 over F has exactly the same four notes).
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="Let It Be — verse progression (C major)"
          caption="C → G → Am → F. Every two counts. Within the F bar, the chord moves from Fmaj7 to Fmaj6 while the bass holds on F. The ending of the verse uses a plagal cadence (F to C)."
          chords={[
            { chord: "C", rn: "I", function: "Tonic" },
            { chord: "G", rn: "V", function: "Dominant" },
            { chord: "Am", rn: "vi", function: "Tonic" },
            { chord: "Fmaj7", rn: "IVmaj7", function: "Pre-dominant" },
            { chord: "Fmaj6", rn: "IVmaj6", function: "Pre-dominant" },
            { chord: "C", rn: "I", function: "Tonic" },
          ]}
        />

        <MTQuote>
          "Heard an A, so you heard D minor very well. But then the bass was still F, so then you
          have to call it D minor over F. But D minor over F, we have a name for it — it's
          F major 6th."
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Four cadences" title="Authentic, plagal, deceptive, half">
        <Prose>
          <p>
            Using "Let It Be" as the teaching vehicle, Jason introduces all four standard cadence
            types. A cadence is a two-chord pair that creates a particular feeling of motion or rest:
          </p>
          <p>
            <strong>Authentic cadence</strong> (also called perfect cadence): 5 going to 1.
            "5 goes to 1 — it's called as the perfect or the authentic cadence." In C major, G
            resolving to C. The strongest and most conclusive resolution in tonal music.
          </p>
          <p>
            <strong>Plagal cadence</strong>: 4 going to 1. "If it's 4 going to 1, it's a plagal
            cadence." In C major, F resolving to C. The end of each verse of "Let It Be" uses
            exactly this. It sounds more open and peaceful than the authentic cadence.
          </p>
          <p>
            <strong>Deceptive cadence</strong>: the classic form is 5 going to 6 instead of 1 —
            in C major, G to A minor. Jason's rule of thumb is broader: "If it's any something
            going to 6 — 6 happens to be the sixth note in the C major scale, which is A or
            A minor — anything goes to 6, we call it deceptive." The ear expects resolution to 1
            but lands on the minor instead.
          </p>
          <p>
            <strong>Half cadence</strong>: anything ending on 5. "Anything which kind of ends at
            5 is said to be a half cadence." The music stops on the dominant — unresolved, waiting.
            "2 to 5 is also called half. Anything which kind of ends at five is a half cadence."
          </p>
        </Prose>

        <TheoryBox label="The four cadences in C major">
          <strong>Authentic:</strong> G → C (5 → 1). Complete, conclusive. ·{" "}
          <strong>Plagal:</strong> F → C (4 → 1). Open, peaceful. ·{" "}
          <strong>Deceptive:</strong> G → Am (5 → 6). Expected C, arrived at Am instead. ·{" "}
          <strong>Half:</strong> Dm → G (2 → 5, or any → 5). Stops on the dominant — unresolved.
          These four types cover almost every cadential moment you will encounter in tonal music.
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="HuS5NuXRb5Y"
        title="Eleanor Rigby — The Beatles"
        caption="Very little harmonic motion — the song lives mostly on E minor and C. The interest comes from the string arrangement, the melody, and the rhythm. Jason describes it as 'almost like an eight-piece orchestra.' There is a lot to figure out."
      />

      <Chapter eyebrow="Eleanor Rigby" title="Almost like an eight-piece orchestra">
        <Prose>
          <p>
            "Eleanor Rigby" is a complete contrast to the chord-oriented approach of "Let It Be."
            Jason's description is direct: "There's almost like an eight-piece orchestra which is
            happening in this song. It's quite interesting. There's a lot to figure out."
          </p>
          <p>
            The string writing is dense and multi-layered. "There's one guy which goes only droning
            E." While that drone sustains, other string parts are doing independent melodic and
            rhythmic figures above and around it. This is what gives the arrangement its orchestral
            weight — multiple independent parts, each doing something different.
          </p>
          <p>
            For students: Jason recommends this song as a later study rather than an early one.
            Transcribing the individual string parts is a serious exercise — isolate each part
            in Moises, listen for the drone, then find the moving lines above it. "We can do
            a couple more later. I can show you how they composed it and move forward."
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Riff songs" title="Twist and Shout, Come Together, Helter Skelter">
        <Prose>
          <p>
            The second half of this lesson moves from ballads to riff-based Beatles songs. Jason
            distinguishes between a melody (usually played in the upper register) and a
            <strong> riff</strong> (a melodic idea in the lower octave, used as a rhythmic and
            harmonic anchor). "A riff is a lower octave tune, you could say."
          </p>
          <p>
            <strong>Twist and Shout</strong> is in D major. The main riff uses the notes A B C♯ D.
            In solfège terms: padhanisa. "In the future, let's say your friend tells you to play
            it on another scale — no problem. Padhanisa will become..." The chord progression
            underneath is I → IV → V → V7 (D → G → A → A7). "The 5, when you play it as a 7,
            this is also known in music theory as it is the primary dominant. Generally all fives
            want to go to one. This five really wants to go to one, especially when you play it as
            a dominant seventh." Three guitarists play different parts in this song: the bass
            player, an electric guitar continuing the riff, and an acoustic guitar playing the
            chords.
          </p>
          <p>
            <strong>Come Together</strong> is in D minor. The bass riff: start on D, then
            G – A – C – back to D. The rhythm divides straight by
            four — not swing, not triplet.
          </p>
          <p>
            <strong>Helter Skelter</strong>: "Apparently this is the song which gave birth to
            heavy metal. This one song." The chords under the main riff are A7 and E7. The guitar
            moves A – E – A – F♯ and then copies that pattern on the E chord. There is a consistent
            G note in the mix creating the A7 colour.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/09-let-it-be-riffs.png"
          caption="Jason's Let It Be chart. Verse: C → G → Am → Fmaj7 → Fmaj6, then C → G → F → C, with the bass line sketched under the first line. Chorus: Am → Em/G → Fmaj7 → C, then C → G → F → C. At the bottom, a descending line in thirds (F A, E G, D F, C E, B♭ D, A C, G B, F A) that comes to rest on C and G."
        />
      </Chapter>

      <Chapter eyebrow="The pick-up bar" title="The first notes may not be on the one">
        <Prose>
          <p>
            While working out the Twist and Shout riff, Jason introduces the concept of the
            <strong> pick-up bar</strong> (also called a pickup measure). Many songs — and many
            riffs — don't begin on beat one of the first full bar. The opening notes happen before
            bar one, leading into it.
          </p>
          <p>
            In Twist and Shout, the riff enters before the actual downbeat. This intro bar is not
            a full bar — "it'll happen only in the beginning. Once this finishes, it's gonna go
            back." The actual repeating loop starts after the pickup. When transcribing, you can
            note the pickup bar as a short incomplete bar and count forward from there.
          </p>
          <p>
            "This is called as a pick-up measure or a pick-up bar. The pick-up bar also need not
            even be notated with all these prior things. You can just say that the pick-up bar is
            one and a half beats long and just play something for one and a half beats." The
            practical lesson: don't assume that because a musician plays something, it's on beat
            one. "The first few notes played by musicians in a song may not actually be on the one
            of the bar. Just because they decide to play something doesn't mean it is the one."
          </p>
        </Prose>

        <MTQuote>
          "This is an important thing, don't get confused. The first few notes played by musicians
          in a song may not actually be on the one of the bar. Just because they decide to play
          something doesn't mean it is the one, right? So that has to be — you have to feel it
          with your head."
        </MTQuote>
      </Chapter>
    </LessonShell>
  );
}
